import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from '@/react-query/api';

interface PatientConsent {
  createdAtISO: Date;
  dob: string | null;
  fullName: string;
  id: string;
}

export interface ClinicalNote {
  createdAt: Date;
  id: string;
  promptName: string | null;
  templateName: string | null;
  summary: string;
  editedSummary: string | null;
  unifiedAppointmentId?: string;
  standalonePatientVisitId?: string;
  updatedAt: Date;
  duration?: number;
  feedbackTraceId?: string;
  rating?: 'up' | 'down';
  user?: {
    firstName: string;
    lastName: string;
  };

  writebackStatus?: VideaNoteWriteBackStatus;
  patientConsent: PatientConsent | null;
}

export interface VideaNoteWriteBackStatus {
  clinicalNoteId: string | null;
  isProcessed: boolean;
  processedAt: Date | null;
  errorCode: string | null;
  errorMessage: string | null;
  result: string | null;
}

const fetchExistingNotes = async ({
  currentPracticeId,
  currentOrganizationId,
  unifiedId,
}: {
  currentPracticeId: string;
  currentOrganizationId: string;
  unifiedId: string;
}) => {
  return makeAPIRequestReactQuery<ClinicalNote[]>(
    `/videa-notes/appointment-notes/${unifiedId}`,
    {},
    'get',
    {
      headers: {
        'Access-Control-Allow-Origin': '*',
        practice: currentPracticeId,
        org: currentOrganizationId,
      },
    },
  );
};

const fetchStandalonePatientVisitNotes = async ({
  currentPracticeId,
  currentOrganizationId,
  standalonePatientVisitId,
}: {
  currentPracticeId: string;
  currentOrganizationId: string;
  standalonePatientVisitId: string;
}) => {
  return makeAPIRequestReactQuery<ClinicalNote[]>(
    `/videa-notes/standalone-patient-visit-notes/${standalonePatientVisitId}`,
    {},
    'get',
    {
      headers: {
        'Access-Control-Allow-Origin': '*',
        practice: currentPracticeId,
        org: currentOrganizationId,
      },
    },
  );
};

export const useExistingNotes = (
  unifiedAppointmentId: string | undefined,
  enabled: boolean,
) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useQuery({
    queryKey: [
      'videa-notes',
      currentOrganizationId,
      currentPracticeId,
      unifiedAppointmentId,
    ],
    queryFn: () => {
      return fetchExistingNotes({
        currentPracticeId,
        currentOrganizationId,
        unifiedId: unifiedAppointmentId!,
      });
    },
    enabled: !!unifiedAppointmentId && enabled,
  });
};

export const useExistingStandalonePatientVisitNotes = (
  standalonePatientVisitId: string | undefined,
  enabled: boolean,
) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useQuery({
    queryKey: [
      'videa-notes',
      currentOrganizationId,
      currentPracticeId,
      standalonePatientVisitId,
    ],
    queryFn: () => {
      return fetchStandalonePatientVisitNotes({
        currentPracticeId,
        currentOrganizationId,
        standalonePatientVisitId: standalonePatientVisitId!,
      });
    },
    enabled: !!standalonePatientVisitId && enabled,
  });
};
